import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { GoogleMap, LoadScript } from '@react-google-maps/api';
import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { googleMapsOptions } from '../../config/googleMapsOptions';
import { Category } from '../../queries/categoriesQuery';
import { Marker } from '../../queries/markersQuery';
import { customCluster } from './customCluster';
import { createMapMarkers } from './utils/createMapMarkers';

interface MapProps {
  markers: Marker[];
  currentCategory: Category;
}

export const Map: React.FC<MapProps> = ({ markers, currentCategory }) => {
  const markerClustererRef = useRef<MarkerClusterer>();
  const mapRef = useRef<google.maps.Map>();
  const { t } = useTranslation();
  const createMarkerClusterer = useCallback(
    (map: google.maps.Map) => {
      return new MarkerClusterer({
        markers: createMapMarkers(markers, currentCategory, map),
        map,
        algorithm: new SuperClusterAlgorithm({ radius: 100, minPoints: 4 }),
        renderer: {
          // move to separate file
          render: function ({ count, position }) {
            return new google.maps.Marker({
              position,
              icon: {
                url: customCluster(currentCategory.color),
                scaledSize: new google.maps.Size(75, 75),
              },
              label: {
                text: String(count),
                color: '#fff',
                fontSize: '12px',
                fontWeight: 'bold',
              },
            });
          },
        },
      });
    },
    [currentCategory]
  );

  const onLoad = useCallback((map: google.maps.Map) => {
    markerClustererRef.current = createMarkerClusterer(map);
    mapRef.current = map;
  }, []);

  useEffect(() => {
    if (!markerClustererRef.current || !mapRef.current) {
      return;
    }

    markerClustererRef.current.clearMarkers();
    markerClustererRef.current = createMarkerClusterer(mapRef.current);
  }, [markers]);

  if (!googleMapsOptions.apiKey) {
    return <p>{t('mapsApiKeyNotProvided')}</p>;
  }

  return (
    <LoadScript googleMapsApiKey={googleMapsOptions.apiKey}>
      <GoogleMap
        mapContainerStyle={{ height: googleMapsOptions.height }}
        center={googleMapsOptions.defaultLocation}
        zoom={googleMapsOptions.zoom}
        onLoad={onLoad}
        options={{
          styles: googleMapsOptions.styles,
          scaleControl: false,
          streetViewControl: false,
          mapTypeControl: false,
        }}
        id="map"
      />
    </LoadScript>
  );
};
